import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { SyncTransactionReportApi } from "../../../../../../../services/Api/Admin/adminTypes";

interface Props {
	isShown: boolean;
	onHide: () => void;
	onSuccessHandle: () => void;
}

const SynchronizeTransactionsReportModal = ({ isShown, onHide, onSuccessHandle }: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<SyncTransactionReportApi>({
		criteriaMode: "all",
	});
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		control,
		handleSubmit,
		setError,
		register,
		formState,
		watch,
		formState: { errors },
	} = form;
	const [types, setTypes] = useState<string[]>([]);
	const [sources, setSources] = useState<string[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const type = watch("type");

	useEffect(() => {
		fetchSyncInfo();
	}, []);

	const getSourceOptions = () =>
		sources.map((source) => ({
			label: t(`enums.transaction_source.source.${source}`),
			value: source,
		}));

	const getTypeOptions = () =>
		types.map((type) => ({
			label: t(`enums.transaction_source.type.${type}`),
			value: type,
		}));

	const fetchSyncInfo = async () => {
		setLoading(true);
		try {
			const res = await adminApi.getTransactionsReportSyncInfo();
			const data = res.data.data;
			setSources(data.sources);
			setTypes(data.types);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const getParsedFormData = (data: SyncTransactionReportApi) => {
		const companyIds =
			data.company_ids && typeof data.company_ids === "string" ? data.company_ids.split(",") : undefined;
		const invoiceIds =
			data.invoice_ids && typeof data.invoice_ids === "string" ? data.invoice_ids.split(",") : undefined;

		return {
			...data,
			company_ids: companyIds,
			invoice_ids: invoiceIds,
		};
	};

	const onSubmit = handleSubmit(async (data) => {
		setSubmitLoading(true);
		try {
			const newData = getParsedFormData(data);
			await adminApi.syncTransactionsReport(newData);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			onHide();
			onSuccessHandle();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setSubmitLoading(false);
	});

	if (loading) return <LoadingContainer />;

	return (
		<Modal onHide={onHide} show={isShown}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.transaction_report.field.synchronize.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormDirty formState={formState} onSubmit={onSubmit}>
					<FormSelectGroup
						errors={errors}
						control={control}
						name="type"
						label={t("modules.transaction_report.field.type.title")}
						options={getTypeOptions()}
						data-testid="type"
					/>
					{(type === "SYNC" || type === "REFRESH" || type === "REFRESH_UPSELLING") && (
						<FormDatePicker errors={errors} name={`date_from`} control={control} label={"Od daty"} />
					)}
					<FormSelectGroup
						errors={errors}
						control={control}
						name="sources"
						isMulti
						label={t("modules.transaction_report.field.sources.title")}
						options={getSourceOptions()}
						data-testid="sources"
					/>
					<FormInput label={"Company ID"} name={"company_ids"} errors={errors} register={register} />
					<FormInput label={"Invoice ID"} name={"invoice_ids"} errors={errors} register={register} />
				</FormDirty>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={submitLoading} type="submit" onClick={onSubmit}>
					{t("modules.transaction_report.action.synchronize.title")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default SynchronizeTransactionsReportModal;

// t("enums.transaction_source.type.FROM_BEGINNING");
// t("enums.transaction_source.type.FROM_LAST_SYNC");
// t("enums.transaction_source.source.CHARGEBEE");
